import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { selectSignage } from '../data/signage.duck';
import { useSignageOrientation } from '../hooks/useSignageOrientation';
import { formatDisplayDate, formatInZone } from './signageTime';

/**
 * White product header for every signage state: company logo left, office name
 * centred, date and clock right. Branding only: no navigation, no menu.
 */
export function SignageTopBar() {
  const signage = useSelector(selectSignage);
  const orientation = useSignageOrientation();
  const portrait = orientation === 'portrait';
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const occupancy = signage.occupancy;
  const clock = useMemo(() => formatInZone(now), [now]);

  // Same resolution as BrandingLogo: logoUrl is a filename under /assets/images/logos/
  const logoSrc = occupancy?.companyLogoUrl
    ? `/assets/images/logos/${occupancy.companyLogoUrl}`
    : '/assets/images/logos/team-today-logo.svg';

  const officeTitle = occupancy?.officeLabel || signage.officeLabel;

  return (
    <Bar $portrait={portrait}>
      <LeftGroup>
        <Logo $portrait={portrait} src={logoSrc} alt={signage.companyName || 'Team Today'} />
      </LeftGroup>

      {officeTitle && (
        <CentreGroup>
          <OfficeIconWrapper><Icon icon={IconTypes.Office} /></OfficeIconWrapper>
          <OfficeTitle $portrait={portrait}>{officeTitle}</OfficeTitle>
        </CentreGroup>
      )}

      <RightGroup>
        {occupancy && <DateText $portrait={portrait}>{formatDisplayDate(occupancy.date)}</DateText>}
        <Clock $portrait={portrait}>{clock.hour}:{clock.minute}</Clock>
      </RightGroup>
    </Bar>
  );
}

const Bar = styled.div<{ $portrait: boolean }>`
  position: relative;
  height: 48px;
  flex-shrink: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  gap: 24px;
  z-index: 20;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const Logo = styled.img<{ $portrait: boolean }>`
  height: ${p => p.$portrait ? 20 : 24}px;
  width: auto;
  display: block;
`;

const CentreGroup = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 40%;
`;

const OfficeIconWrapper = styled.span`
  color: ${Colours.black};
  display: inline-flex;
  font-size: 20px;
`;

const OfficeTitle = styled.div<{ $portrait: boolean }>`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: ${p => p.$portrait ? 17 : 20}px;
  line-height: ${p => p.$portrait ? 19 : 22}px;
  letter-spacing: -0.01em;
  color: ${Colours.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const DateText = styled.div<{ $portrait: boolean }>`
  font-family: "proxima-nova", sans-serif;
  font-weight: 400;
  font-size: ${p => p.$portrait ? 14 : 16}px;
  color: ${Colours.darkGrey};
  white-space: nowrap;
`;

const Clock = styled.div<{ $portrait: boolean }>`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: ${p => p.$portrait ? 24 : 28}px;
  line-height: ${p => p.$portrait ? 24 : 28}px;
  color: ${Colours.black};
  font-variant-numeric: tabular-nums;
`;
